import React, { useState, useEffect } from 'react';
import CountryFlag from '../common/countryFlag/country-flag';
import ProgressBar from '../common/progressBar/ProgressBar';
import IconPlus from '../common/icon/icon-plus';
import IconMinus from '../common/icon/icon-minus';
import { formatRank } from '../../utils/utils';
import { resilienceConfig } from '../../config/project-config';
import styles from './GlobalResilienceIndex.module.scss';

const CountryRow = ({
  country,
  sortConfig,
  getColumnValue,
  expandedRows,
  toggleRowExpansion,
  expandedSections,
  toggleSectionExpansion,
  hasActiveFilter
}) => {
  const riskSectionId = `${country.code}-risk`;
  const resilienceSectionId = `${country.code}-resilience`;

  // Check if mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 799);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const progressColspan = isMobile ? 3 : 1;

  // Helper function to render metric rows
  const renderMetricRows = (metrics, barColor, sectionId, introText, introTextMobile) => {
    // Only render if the section is expanded
    if (!expandedSections.has(sectionId)) return null;

    return (
      <>
        {/* Intro text row */}
        <tr className={`${styles.expandedRow} ${styles.level2}`}>
          <td colSpan="2" className={styles.metricLabelCell}><span className={styles.sectionTitle}>{introText}</span></td>
            <td colSpan={progressColspan} className={styles.metricProgressCell}>
                <span className={`${styles.sectionTitle} d-md-none`}>{introTextMobile || introText}</span>
            </td>
            <td className={styles.expandColumn}></td>
            <td className={styles.filterCell}></td>
        </tr>
        {metrics.map((metric, index) => {
          const value = country[metric.key];
          const percentValue = Math.round(value * 100);
          const isLast = index === metrics.length - 1;
          return (
            <tr key={metric.key} className={`${styles.expandedRow} ${styles.level2} ${isLast ? styles.lastMetric : ''}`}>
              <td colSpan="2" className={styles.metricLabelCell}><span className={styles.subSectionTitle}>{metric.label}</span></td>
              <td colSpan={progressColspan} className={styles.metricProgressCell}>
                  <span className={`${styles.subSectionTitle} d-md-none`}>{metric.label}</span>
                <ProgressBar
                  percentValue={percentValue}
                  labelText={value.toFixed(2)}
                  height={8}
                  barColor={barColor}
                />
              </td>
              <td className={styles.expandColumn}></td>
              <td  className={styles.filterCell}></td>
            </tr>
          );
        })}
      </>
    );
  };

  return (
    <>
      {/* Main Country Row */}
      <tr className={expandedRows.has(country.code) ? styles.baseLevel : ''}>
        <td className={styles.rankCell}>
          {formatRank(country.globalRank)}
        </td>
        <td className={`${styles.countryCell} ${sortConfig.key === 'country' ? styles.selectedColumn : ''}`}>
            <div className={styles.countryNameWrapper} onClick={() => toggleRowExpansion(country.code)}>
              <CountryFlag countryName={country.country} code={country.iso2} />
            </div>
        </td>
        <td className={`${styles.scoreCell} ${sortConfig.key === 'totalScore' ? styles.selectedColumn : ''} ${hasActiveFilter ? styles.mobileFilterActive : ''}`}>
          <span className={styles.scoreValue}>{country.totalScore ? country.totalScore.toFixed(2) : 'N/A'}</span>
          <span className={styles.filterValue}>{getColumnValue(country)}</span>
        </td>
        <td className={styles.expandColumn}>
          <button
            className={styles.expandButton}
            onClick={() => toggleRowExpansion(country.code)}
            aria-label={expandedRows.has(country.code) ? 'Collapse details' : 'Expand details'}
          >
            {expandedRows.has(country.code) ? <IconMinus /> : <IconPlus />}
          </button>
        </td>
        <td className={styles.filterCell}>
          {getColumnValue(country)}
        </td>
      </tr>

      {/* Expanded Content - Only render if country row is expanded */}
      {expandedRows.has(country.code) && (
        <>
          {/* Risk Score Header Row */}
          <tr className={styles.expandedRow}>
              <td colSpan="2" className={styles.metricLabelCell}>
                  <span className={styles.sectionTitle}>
                      <span>Total Risk Score</span><br />
                      <span className={styles.sectionSubTitle}>(Out of 100, Lower is Better)</span>
                  </span>
              </td>
                <td colSpan={progressColspan} className={styles.metricProgressCell}>
                    <span
                      className={`${styles.sectionTitle} ${styles.mobileSectionTitle} d-md-none`}
                      onClick={() => toggleSectionExpansion(riskSectionId)}
                    >
                        <span className={styles.mobileSectionTitleText}>Total Risk Score</span><br /><span className={styles.mobileSectionTitleSubText}>(Out of 100, Lower is Better)</span>
                    </span>
                    <ProgressBar
                        percentValue={country.riskScore}
                        height={12}
                        labelText={country.riskScore}
                        barColor={resilienceConfig.colors.risk}
                    />
            </td>
              <td className={styles.expandColumn}>
                  <button className={styles.expandButton} onClick={() => toggleSectionExpansion(riskSectionId)}>
                    {expandedSections.has(riskSectionId) ? <IconMinus /> : <IconPlus />}
                  </button>
              </td>
              <td className={styles.filterCell}></td>
          </tr>

          {/* Risk Detail Rows */}
          {renderMetricRows(
            resilienceConfig.riskMetrics,
            resilienceConfig.colors.riskDetail,
            riskSectionId,
            <><span className={styles.mobileSectionSubtitle}>Risk Indicators</span><br /><span className={styles.mobileSectionTitleSubText}>(0‒1 Scale, Lower is Better)</span></>,
            <><span className={styles.mobileSectionSubtitle}>Risk Indicators</span><br /><span className={styles.mobileSectionTitleSubText}>(0‒1 Scale, Lower is Better)</span></>
          )}

          {/* Resilience Score Header Row */}
          <tr className={styles.expandedRow}>
              <td colSpan="2" className={styles.metricLabelCell}>
                  <span className={styles.sectionTitle}>
                      <span>Total Resilience Score</span><br />
                       <span className={styles.sectionSubTitle}>(Out of 100, Higher is Better)</span>
                  </span>
              </td>
            <td colSpan={progressColspan} className={`${styles.metricProgressCell}`}>
                <span
                  className={`${styles.sectionTitle} d-md-none`}
                  onClick={() => toggleSectionExpansion(resilienceSectionId)}
                >
                    <span className={styles.mobileSectionTitleText}>Total Resilience Score</span><br /><span className={styles.mobileSectionTitleSubText}>(Out of 100, Higher is Better)</span>
                </span>
                  <ProgressBar
                      percentValue={country.resilienceScore}
                      labelText={country.resilienceScore}
                      height={12}
                      barColor={resilienceConfig.colors.resilience}
                  />
            </td>
              <td  className={styles.expandColumn}>
                   <button className={styles.expandButton} onClick={() => toggleSectionExpansion(resilienceSectionId)}>
                    {expandedSections.has(resilienceSectionId) ? <IconMinus /> : <IconPlus />}
                  </button>
              </td>
              <td className={styles.filterCell}></td>
          </tr>

          {/* Resilience Detail Rows */}
          {renderMetricRows(
            resilienceConfig.resilienceMetrics,
            resilienceConfig.colors.resilienceDetail,
            resilienceSectionId,
            <><span className={styles.mobileSectionSubtitle}>Resilience Indicators</span><br /><span className={styles.mobileSectionTitleSubText}>(0‒1 Scale, Higher is Better)</span></>,
            <><span className={styles.mobileSectionSubtitle}>Resilience Indicators</span><br /><span className={styles.mobileSectionTitleSubText}>(0‒1 Scale, Higher is Better)</span></>
          )}
        </>
      )}
    </>
  );
};

export default CountryRow;